import { BookOpen, Search, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useDocs } from "../../app/docs-context";
import type { SearchHit } from "../../shared/api/types";
import { Excerpt } from "../../shared/ui/excerpt";

/** Debounce so a fast typist does not fire a full-text query per keystroke. */
const SEARCH_DEBOUNCE_MS = 180;

/**
 * The ⌘K search overlay.
 *
 * Results come from Postgres full-text search, already filtered to what this
 * reader may open. The snippet is `ts_headline` output with `<mark>` around the
 * matches; it is rendered as text plus `<mark>` elements, never as HTML (see Excerpt).
 */
export function SearchDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [query, setQuery] = useState("");
  const [results, setHits] = useState<SearchHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { api, paths } = useDocs();

  // The shell mounts this only while it is open, so closing discards the previous search and
  // reopening never flashes it.
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open || query.trim().length < 2) return;

    const controller = new AbortController();
    const timer = setTimeout(() => {
      setLoading(true);
      api
        .search(query.trim(), controller.signal)
        .then((found) => {
          setHits(found ?? []);
          setActiveIndex(0);
        })
        .catch(() => setHits([]))
        .finally(() => setLoading(false));
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query, open, api]);

  if (!open) return null;

  // Under two characters there is no search, whatever the last one returned.
  const hits = query.trim().length < 2 ? [] : results;

  const goTo = (hit: SearchHit) => {
    void navigate(paths.article(hit.slug));
    onClose();
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      onClose();
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, hits.length - 1));
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
      return;
    }
    if (event.key === "Enter" && hits[activeIndex]) {
      event.preventDefault();
      goTo(hits[activeIndex]);
    }
  };

  return (
    <div
      className="fixed inset-0 z-modal flex items-start justify-center p-4 pt-[12vh] bg-slate-1000/60"
      // Only a click on the backdrop itself closes; the keys bubble here from the input and results.
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      onKeyDown={onKeyDown}
      role="presentation"
    >
      <div
        className="w-full max-w-2xl overflow-hidden rounded-lg border shadow-lg"
        style={{ background: "var(--card)", borderColor: "var(--border)" }}
        role="dialog"
        aria-modal="true"
        aria-label="Search documentation"
      >
        <div
          className="flex items-center gap-3 border-b px-4"
          style={{ borderColor: "var(--border)" }}
        >
          <Search
            className="size-4 shrink-0"
            style={{ color: "var(--muted-foreground)" }}
            aria-hidden
          />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search documentation…"
            className="h-14 flex-1 bg-transparent text-base outline-none"
            style={{ color: "var(--foreground)" }}
            aria-label="Search documentation"
          />
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer p-1"
            style={{ color: "var(--muted-foreground)" }}
            aria-label="Close search"
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {loading && (
            <div className="px-4 py-6 text-sm" style={{ color: "var(--muted-foreground)" }}>
              Searching…
            </div>
          )}

          {!loading && query.trim().length >= 2 && hits.length === 0 && (
            <div className="px-4 py-6 text-sm" style={{ color: "var(--muted-foreground)" }}>
              No documents match “{query.trim()}”.
            </div>
          )}

          {!loading && query.trim().length < 2 && (
            <div className="px-4 py-6 text-sm" style={{ color: "var(--muted-foreground)" }}>
              Type at least two characters to search.
            </div>
          )}

          {hits.map((hit, index) => (
            <button
              key={hit.slug}
              type="button"
              onClick={() => goTo(hit)}
              onMouseEnter={() => setActiveIndex(index)}
              className="flex w-full cursor-pointer items-start gap-3 border-b px-4 py-3 text-left last:border-b-0"
              style={{
                borderColor: "var(--border)",
                background: index === activeIndex ? "var(--muted)" : "transparent",
              }}
            >
              <BookOpen
                className="mt-0.5 size-4 shrink-0"
                style={{ color: "var(--muted-foreground)" }}
                aria-hidden
              />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium" style={{ color: "var(--foreground)" }}>
                  {hit.title}
                </span>
                {hit.excerpt && (
                  <span
                    className="mt-0.5 block text-xs leading-relaxed"
                    style={{ color: "var(--muted-foreground)" }}
                  >
                    <Excerpt
                      text={hit.excerpt}
                      markClassName="bg-transparent font-semibold text-[var(--foreground)]"
                    />
                  </span>
                )}
              </span>
              <span className="shrink-0 text-xs" style={{ color: "var(--muted-foreground)" }}>
                {hit.categoryName}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Opens the dialog on ⌘K / Ctrl-K, the shortcut readers expect from docs sites. */
export function useSearchShortcut(onOpen: () => void): void {
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        onOpen();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onOpen]);
}
